import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import ConfirmModal from './common/ConfirmModal';
import './BlogManager.css';

const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image: '',
    author: 'Admin',
    category: 'Art',
    tags: [],
    isPublished: false,
  });
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/blogs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlogs(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    // Build query string for upload type and blog category
    let queryString = '?uploadType=blog';
    if (formData.category) {
      queryString += `&blogCategory=${encodeURIComponent(formData.category)}`;
    }

    try {
      const response = await axios.post(`${API_URL}/api/upload${queryString}`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      setFormData({ ...formData, image: response.data.data.baseS3Url || response.data.data.url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((t) => t !== tag),
    });
  };

  const handleSubmit = async (e) => {
    // Prevent any default form behavior and event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return false;
      }
      
      if (editingBlog) {
        await axios.put(
          `${API_URL}/api/blogs/${editingBlog._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Blog updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/blogs`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Blog created successfully!');
      }
      fetchBlogs();
      resetForm();
    } catch (error) {
      console.error('Blog submit error:', error);
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error(error.response?.data?.message || 'Operation failed');
      }
      // Don't reset form on error - keep form data so user can fix and retry
      return false;
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      excerpt: blog.excerpt || '',
      content: blog.content,
      image: blog.image,
      author: blog.author || 'Admin',
      category: blog.category || 'Art',
      tags: blog.tags || [],
      isPublished: blog.isPublished || false,
    });
    setShowForm(true);
  };

  const handleTogglePublish = async (blog) => {
    try {
      const newPublishedStatus = !blog.isPublished;
      await axios.put(
        `${API_URL}/api/blogs/${blog._id}`,
        { isPublished: newPublishedStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Blog ${newPublishedStatus ? 'published' : 'unpublished'} successfully!`);
      fetchBlogs();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update blog status');
    }
  };

  const handleDelete = (id) => {
    setConfirmModal({
      show: true,
      title: 'Delete Blog',
      message: 'Are you sure you want to delete this blog? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/blogs/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          toast.success('Blog deleted successfully!');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
          fetchBlogs();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete blog');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      image: '',
      author: 'Admin',
      category: 'Art',
      tags: [],
      isPublished: false,
    });
    setTagInput('');
    setEditingBlog(null);
    setShowForm(false);
  };

  return (
    <div className="blog-manager">
      <div className="header">
        <h2>Blog Manager</h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Cancel' : '+ Add Blog'}
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h3>{editingBlog ? 'Edit Blog' : 'Add New Blog'}</h3>
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e);
            }} 
            noValidate
          >
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Excerpt (Max 200 chars)</label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                rows="3"
                maxLength="200"
              />
              <small>{formData.excerpt.length}/200 characters</small>
            </div>

            <div className="form-group">
              <label>Content *</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows="10"
                required
              />
            </div>

            <div className="form-group">
              <label>Image *</label>
              <div style={{ marginBottom: '10px', padding: '10px', background: '#f0f7ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                <strong style={{ display: 'block', marginBottom: '5px', color: '#0066cc' }}>📐 Ideal Image Size:</strong>
                <span style={{ fontSize: '13px', color: '#333' }}>
                  <strong>Blog Image:</strong> 1200 x 384 pixels (approximately 3:1 ratio) - Displayed as header image on blog detail page (h-96 = 384px height)
                </span>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={uploading}
              />
              {uploading && <p>Uploading...</p>}
              {formData.image && (
                <div className="image-preview">
                  <img src={formData.image} alt="Preview" />
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Author</label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="Art">Art</option>
                  <option value="Vastu">Vastu</option>
                  <option value="Home Decor">Home Decor</option>
                  <option value="Spiritual">Spiritual</option>
                  <option value="Tips">Tips</option>
                  <option value="News">News</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Tags</label>
              <div className="tag-input-group">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Add tag and press Enter"
                />
                <button type="button" onClick={handleAddTag} className="btn-add-tag">
                  Add
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="tags-list">
                  {formData.tags.map((tag, idx) => (
                    <span key={idx} className="tag">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="tag-remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isPublished}
                  onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                />
                Publish (make visible to public)
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingBlog ? 'Update' : 'Create'} Blog
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p>Loading blogs...</p>
      ) : (
        <div className="blogs-grid">
          {blogs.map((blog) => (
            <div key={blog._id} className="blog-card">
              {blog.image && <img src={blog.image} alt={blog.title} />}
              <div className="blog-info">
                <h4>{blog.title}</h4>
                <p className="blog-category">Category: {blog.category}</p>
                <p className="blog-status">
                  Status: {blog.isPublished ? 'Published' : 'Draft'}
                </p>
                {blog.excerpt && <p className="blog-excerpt">{blog.excerpt}</p>}
                <div className="blog-actions">
                  <button 
                    onClick={() => handleTogglePublish(blog)} 
                    className={blog.isPublished ? "btn-unpublish" : "btn-publish"}
                    title={blog.isPublished ? "Click to unpublish" : "Click to publish"}
                  >
                    {blog.isPublished ? 'Unpublish' : 'Publish'}
                  </button>
                  <button onClick={() => handleEdit(blog)} className="btn-edit">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(blog._id)} className="btn-delete">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' })}
        confirmText="Delete"
        cancelText="Cancel"
        type={confirmModal.type}
      />
    </div>
  );
};

export default BlogManager;

